import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate hook
import { motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import * as Yup from 'yup'; // For validation schema
import TabbedFormComponent from '../common/TabbedFormComponent'
import AdvancedDataTableComponent from '../common/AdvancedDataTableComponent'
import { registerUser, fetchClientDataById, updateClient, fetchClientData } from '../../services/adminApiService'; // Adjust the path as necessary
import { fetchCountries, fetchStatesByCountry } from '../../services/commonApiService'; // Adjust the path as necessary
import FloatingLabelInput from '../ui/FloatingLabelInput';
import FloatingLabelSelect from '../ui/FloatingLabelSelect';
import FloatingLabelTextarea from '../ui/FloatingLabelTextarea';
import FloatingLabelDatePicker from '../ui/FloatingLabelDatePicker';
import AlternateDatePicker from '../ui/AlternateDatePicker';


const ClientForm = ({ selectedClient, onClientUpdated }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [countriesLoaded, setCountriesLoaded] = useState(false); // Track if countries are loaded
    const [states, setStates] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);

    const [formValues, setFormValues] = useState({
        clientId: '',
        clientName: '',
        firmName: '',
        brand: '',
        contactNumber: '',
        email: '',
        countryId: '',
        stateId: '',
        agreementDate: null,
        renewalDate: null,
        address: '',
    });

    const [errors, setErrors] = useState({});

    // Simple validation function
    const validate = () => {
        const newErrors = {};
        if (!formValues.clientName) newErrors.clientName = 'Organization Name is required';
        if (!formValues.firmName) newErrors.firmName = 'Firm Name is required';
        if (!formValues.brand) newErrors.brand = 'Brand is required';
        if (!/^[0-9]{10}$/.test(formValues.contactNumber)) {
            newErrors.contactNumber = 'Phone number must be exactly 10 digits';
        }
        if (!/\S+@\S+\.\S+/.test(formValues.email)) {
            newErrors.email = 'Email is required and must be valid';
        }
        if (!formValues.countryId) newErrors.countryId = 'Country is required';
        if (!formValues.stateId) newErrors.stateId = 'State is required';
        if (!formValues.address) newErrors.address = 'Address is required';

        return newErrors;
    };

    const handleBlur = (field) => {
        const validationErrors = validate();
        setErrors(validationErrors);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        // Validate each field in real-time
    //const validationErrors = validate(); // Re-validate all fields
        //setErrors({ ...errors, [name]: '' }); // Clear error for the field being changed
       // setErrors(validationErrors); // Update errors state with any validation messages
        
    };

    const getCountries = async () => {
        try {
            const countryData = await fetchCountries();
            setCountries(countryData.map(country => ({ value: country.id, label: country.name })));
        } catch (error) {
            console.error("Error fetching countries:", error);
        }
    };

    const inputClasses = (field) =>
        errors[field] ? 'border-red-500' : 'border-gray-300';
    
    
    useEffect(() => {
        getCountries();
    }, []);

    const fetchStates = async (countryId) => {
        try {
            const stateData = await fetchStatesByCountry(countryId);
            setStates(stateData.map(state => ({ value: state.id, label: state.name })));
        } catch (error) {
            console.error("Error fetching states:", error);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
        if (selectedClient && selectedClient.clientId!="") {
            setIsEditing(true);
            setFormValues({
                clientId: selectedClient.clientId || '',
                clientName: selectedClient.clientName || '',
                firmName: selectedClient.firmName || '',
                brand: selectedClient.brand || '',
                contactNumber: selectedClient.contactNumber || '',
                email: selectedClient.email || '',
                countryId: selectedClient.countryId || '',
                stateId: selectedClient.stateId || '',
                agreementDate: selectedClient.agreementDate || null,
                renewalDate: selectedClient.renewalDate || null,
                address: selectedClient.address || ''
            });
            setFormValues(prevValues => ({
                ...prevValues,
                countryId: selectedClient.countryId,
                stateId: selectedClient.stateId,
                agreementDate: selectedClient.agreementDate ? new Date(selectedClient.agreementDate) : null,
                renewalDate: selectedClient.renewalDate ? new Date(selectedClient.renewalDate) : null,
            }));
            if (selectedClient.countryId) {
                fetchStates(selectedClient.countryId).then(() => {
                    setFormValues(prevValues => ({
                        ...prevValues,
                        stateId: selectedClient.stateId || null
                    }));
                });
            } else {
                setStates([]); // Clear states if no country is selected
            }
        }else{
            setIsEditing(false);
        }
    }, 500); // 3000ms = 3 seconds

    return () => clearTimeout(timer);
    }, [selectedClient, countries]); // Ensure to re-run when countries change



    const handleSubmit = async (e) => {

        e.preventDefault();
        const validationErrors = validate();
        // const isValid = await handleValidation(formValues);
        // if (!isValid) return; 
        if (Object.keys(validationErrors).length === 0) {
        try {
            // await validationSchema.validate(formValues, { abortEarly: false });
         
             //   console.log('Form submitted', formValues);
            // setErrors({});
        setLoading(true);
            let data;
            if (isEditing) {
                data = await updateClient(formValues);
            } else {
                data = await registerUser(formValues);
            }
            
            setApiResponse(data);
            if(!data.error){
            resetFormValues();
            onClientUpdated(null); 
            setIsEditing(false);
            setCountries([]); 
            setStates([]);
            }else{
                return;
            }
        } catch (err) {
            const validationErrors = {};
            err.inner.forEach(error => {
                validationErrors[error.path] = error.message;
            });
            setErrors(validationErrors);
            setApiResponse({ err: 'Failed to submit form. Please try again.' });
        } finally {
            setLoading(false);
        }
        setErrors({});
    } else {
        setErrors(validationErrors);
    }
    };
    const resetFormValues = ()=> {
        setFormValues({
            clientId: '',
            clientName: '',
            firmName: '',
            brand: '',
            contactNumber: '',
            email: '',
            countryId: '',
            stateId: '',
            agreementDate: null,
            renewalDate: null,
            address: '',
        }); 
        setIsEditing(false);
        setErrors({});
        onClientUpdated(null); 
        //setApiResponse(null);
    }
    const navigate = useNavigate(); // Initialize useNavigate

    const handleCancel = () => {
        resetFormValues();
        onClientUpdated(null); 
        setStates([]);
        // navigate('/clientRegistration'); // Replace with your desired URL
        // window.location.reload();
    };
    return (
        <div className="container mx-auto p-6 bg-white rounded-lg border">
                        <form  onSubmit={handleSubmit} className="space-y-6 w-full">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {isEditing ?
                                    <input
                                        type="hidden"
                                        name="clientId"
                                        value={formValues.clientId}
                                        id="ClientId"
                                        autoComplete="off"
                                        readOnly={true}
                                        onChange={handleChange}
                                    /> : ''
                                }
                               
                                <div>
                                    <FloatingLabelInput
                                        label="Organization"
                                        id="clientName"
                                        name="clientName"
                                        value={formValues.clientName}
                                        onChange={handleChange}
                            onBlur={() => handleBlur('clientName')}
                            className={inputClasses('clientName')}

                                    />
                                     {errors.clientName && <div className="text-red-500 text-sm">{errors.clientName}</div>}
                                </div>

                                <div>
                                    <FloatingLabelInput
                                        label="Firm Name"
                                        id="firmName"
                                        name="firmName"
                                        value={formValues.firmName}
                                        onChange={handleChange}
                            onBlur={() => handleBlur('firmName')}
                            className={inputClasses('firmName')}

                                    />
                                    {errors.firmName && <div className="text-red-500 text-sm">{errors.firmName}</div>}
                                    </div>

                                <div>
                                    <FloatingLabelInput
                                        label="Brand"
                                        id="brand"
                                        name="brand"
                                        value={formValues.brand}
                                        onChange={handleChange}
                            onBlur={() => handleBlur('brand')}
                            className={inputClasses('brand')}

                                    />
                                   {errors.brand && <div className="text-red-500 text-sm">{errors.brand}</div>}
                                   </div>

                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <FloatingLabelInput
                                        label="Email"
                                        id="email"
                                        name="email"
                                        value={formValues.email}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('email')}
                                        className={inputClasses('email')}
                                    />
                                    {errors.email && <div className="text-red-500 text-sm">{errors.email}</div>}
                                    </div>

                                <div>
                                    <FloatingLabelInput
                                        label="Phone Number"
                                        id="contactNumber"
                                        name="contactNumber"
                                        minlen={10}
                                        maxlen={10}
                                        value={formValues.contactNumber}
                                        onChange={handleChange}
                            onBlur={() => handleBlur('contactNumber')}
                            className={inputClasses('contactNumber')}

                                    />
                                    {errors.contactNumber && <div className="text-red-500 text-sm">{errors.contactNumber}</div>}
                                    </div>
                                <div>
                                    {loading ? (
                                        <p>Loading countries...</p>
                                    ) : (
                                        <FloatingLabelSelect
                                            label="Select Country"
                                            id="countryId"
                                             name="countryId"
                                            value={countries.find(country => country.value === formValues.countryId) || null} // Handle no selection case with null
                                            onChange={(option) => {
                                                setFormValues({ ...formValues, countryId: option.value, stateId: '' }); // Reset stateId
                                                fetchStates(option.value); // Fetch states for selected country
                                            }}
                            onBlur={() => handleBlur('countryId')}
                            className={inputClasses('countryId')}
                                            options={countries}
                                            isSearchable={true}
                                        />
                                    )}
                                   {errors.countryId && <div className="text-red-500 text-sm">{errors.countryId}</div>}
                                  {/* <input value={JSON.stringify(countries.find(country => country.value === formValues.countryId) || 'ACD')}/> */}
                                   
                                   </div>

                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <>
                                        {loadingStates ? (
                                            <p>Loading states...</p>
                                        ) : (
                                            <FloatingLabelSelect
                                                label="Select State"
                                                id="stateId"
                                                 name="StateId"
                                                value={states.find(state => state.value === formValues.stateId) || null} // Use null if no state is selected
                                                onChange={(option) => setFormValues({ ...formValues, stateId: option.value })}
                                               
                            onBlur={() => handleBlur('StateId')}
                            className={inputClasses('StateId')}
                                                options={states}
                                                isSearchable={true}
                                            />

                                        )}
                                    </>
                                    {errors.stateId && <div className="text-red-500 text-sm">{errors.stateId}</div>}
                                    </div>
                                <div>
                                    <AlternateDatePicker
                                        label="Select Agreement Date"
                                        id="AgrrementDate"
                                        name="agreementDate"
                                        value={formValues.agreementDate ? new Date(formValues.agreementDate) : null}
                                        onChange={(date) => {
                                            setTimeout(() => {
                                                if (date instanceof Date && !isNaN(date)) {  // Check if valid date
                                                    setFormValues({ ...formValues, agreementDate: date });
                                                } else {
                                                    setFormValues({ ...formValues, agreementDate: null });  // Clear the value if invalid
                                                }
                                            }, 0);
                                        }} 
                            onBlur={() => handleBlur('agreementDate')}
                            className={inputClasses('agreementDate')}
                                        />
                                </div>
                                <div>
                                    <AlternateDatePicker
                                        label="Select Renewal Date"
                                        id="renewalDate"
                                        name="renewalDate"
                                        value={formValues.renewalDate ? new Date(formValues.renewalDate) : null}
                                        onChange={(date) => {
                                            setTimeout(() => {
                                                if (date instanceof Date && !isNaN(date)) {  // Check if valid date
                                                    setFormValues({ ...formValues, renewalDate: date });
                                                } else {
                                                    setFormValues({ ...formValues, renewalDate: null });  // Clear the value if invalid
                                                }
                                            }, 0);
                                        }} 
                            onBlur={() => handleBlur('renewalDate')}
                            className={inputClasses('renewalDate')}
                                        />
                                </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                                <div>
                                    <FloatingLabelTextarea
                                        label="Address"
                                        id="address"
                                        name="address"
                                        value={formValues.address}
                                        onChange={handleChange}
                                        onBlur={() => handleBlur('address')}
                                        className={inputClasses('address')}
                                    />
                                    {errors.address && <div className="text-red-500 text-sm">{errors.address}</div>}
                                    </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="mt-4">
                                <button
                                    type="submit"
                                    className={`px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 ${loading  ? 'opacity-50' : ''}`}
                                    disabled={loading }
                                >
                                    {loading  ? 'Submitting...' : selectedClient ? 'Update' : 'Submit'}
                                </button>
                            </div>
                            <div className="mt-4">
                                <button
                                    type="button"
                                    onClick={handleCancel } // This is how you reset the form
                                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className="mt-4"></div>
                            </div>
                            {apiResponse && (
                <motion.div
                    className={`mt-4 flex items-center ${apiResponse.error ? 'text-red-500' : 'text-green-500'}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {apiResponse.error ? (
                        <>
                            <FaExclamationCircle className="mr-2" />
                            {apiResponse.error}
                        </>
                    ) : (
                        <>
                            <FaCheckCircle className="mr-2" />
                            {apiResponse.success}
                        </>
                    )}
                </motion.div>
            )}
                        </form>
        </div>
    );
};

const ClientListTable = ({ onSelectClient, switchToFormTab }) => {
    const tableHeaders = ["id", "Organization Name", "Firm Name", "Client Code", "CreatedAt"];

    const [clientList, setClientList] = useState([]);  // Store Clients

    const [loading, setLoading] = useState(false);

    // Fetch Clients when the component loads
    useEffect(() => {
        const getClientData = async () => {
            try {
                const clients_Data = await fetchClientData();  // Fetch countries from service
                const clientOptions = clients_Data.map((client) => ({
                    id: client.clientId,
                    clientName: client.clientName,
                    firmName: client.firmName,
                    clientCode: client.clientCode,
                    createdAt: client.createdAt
                }));
                setClientList(clientOptions);  // Set the countries as options for the select dropdown

            } catch (error) {
                console.error("Error fetching countries:", error);
            } finally {
                // setLoadingCountries(false);  // Stop loading spinner
            }
        };

        getClientData();
    }, []);


    // Mapping of headers to data keys
    const headerMapping = {
        "id": "id",
        "Organization Name": "clientName", // Maps to the data key 'clientName'
        "Firm Name": "firmName", // Assuming you have 'email' key in your data. If not, replace accordingly
        "Client Code": "clientCode", // Maps to the data key 'clientCode'
        "CreatedAt": "createdAt" // Maps to the data key 'createdAt'
    };


    const handleEdit = async (rowObject) => {
        try {
            setLoading(true); // Show loading spinner

            // Fetch client data based on the row object (e.g., client ID)
            const client = await fetchClientDataById(rowObject.id);
            var clientData = ({
                clientId: client.clientId,
                clientName: client.clientName,
                firmName: client.firmName,
                agreementDate: client.clientDetailsEntity.agreementDate,
                brand: client.clientDetailsEntity.brand,
                contactNumber: client.clientDetailsEntity.contactNumber,
                email: client.clientDetailsEntity.email,
                currency: '',
                renewalDate: client.clientDetailsEntity.renewalDate,
                countryId: client.clientDetailsEntity.countryId,
                stateId: client.clientDetailsEntity.stateId,
                logoExist: false,
                status: 'ACTIVE',
                address: client.clientDetailsEntity.address,

            });
            // Populate the form with the fetched client data
            onSelectClient(clientData);

            // Switch to the form tab
            switchToFormTab();

        } catch (error) {
            console.error('Error fetching client data:', error);
        } finally {
            setLoading(false); // Hide loading spinner
        }
    };


    return (
        <div className="container mx-auto p-6 bg-white rounded-lg border">
            {/* <AdvancedDataTableComponent
                headers={tableHeaders}
                data={clientList}
                headerMapping={headerMapping}
                onEdit={handleEdit}
                onDelete={handleDelete}
                showActions={true} // Show edit/delete buttons
            /> */}

            <AdvancedDataTableComponent
                headers={tableHeaders}
                data={clientList}
                headerMapping={headerMapping}
                onEdit={handleEdit}
                showActions={true}
            />

            {/* <AdvancedDataTableComponent
        columns={tableHeaders}
        data={clientList}
        onEdit={handleEdit}
        onDelete={handleDelete}
        showActions={true}
      /> */}
        </div>
    );

};


const TestPage = () => {

    const [selectedClient, setSelectedClient] = useState(null); // Track selected client for editing
    const [activeTab, setActiveTab] = useState(0); // Track active tab

    const handleEdit = (client) => {
        setSelectedClient(client); // Set the selected client data
        setActiveTab(0); // Switch to the "ADD" tab
    };
    // Callback to clear the selected client
    const handleClientUpdated = () => {
        setSelectedClient(null); // Clear the selected client after submit/update
    };
    return (
    <div>
        <h2></h2>
        <TabbedFormComponent
        formContent={(switchToFormTab) => (
            <ClientForm
                selectedClient={selectedClient}
                onClientUpdated={handleClientUpdated} // Pass the callback here
                switchToFormTab={switchToFormTab}
            />
        )}
        dataTableComponent={(switchToFormTab) => (
            <ClientListTable
                onSelectClient={setSelectedClient}
                switchToFormTab={switchToFormTab}
            />
        )}
    />
    </div>
    )
    
};

export default TestPage;
